import React, {createContext, useEffect, useRef, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import Masthead from './containers/Masthead';
import WordsPanel from './containers/WordsPanel';
import Board from './containers/Board';
import {ANSWERING_GAME_MODE} from './constants';
import LetterBox from './components/LetterBox';
import {enterIdleMode, cancelConfirmation, confirmPendingAction} from './actions/actions';
import Backdrop from './components/Backdrop';
import ConfirmationModal from './components/ConfirmationModal';
import './App.scss';
import IntroModal from './components/IntroModal';
import WordSearchGameState from './mobx/WordSearchGameState';

const wordSearchGameState = new WordSearchGameState();
export const StateContext = createContext();

const App = () => {
    const gameMode = useSelector((state) => state.gameMode);
    const collectedLetters = useSelector((state) => state.collectedLetters);
    const pendingConfirmation = useSelector((state) => state.pendingConfirmation);
    const [showIntro, setShowIntro] = useState(true);
    const floatingLettersRefs = useRef([]);
    const dispatch = useDispatch();
    const lastMousePosition = useRef({x: 0, y: 0});
    const rAFIndex = useRef(0);

    function onMouseUp(event) {
        registerMousePosition(event);
        if (gameMode === ANSWERING_GAME_MODE) {
            // Return to idle mode
            dispatch(enterIdleMode());
        }
    }

    function onCancelConfirmation() {
        dispatch(cancelConfirmation());
    }

    function onConfirmPendingAction() {
        dispatch(confirmPendingAction());
    }

    function onIntroClose() {
        setShowIntro(false);
    }

    function registerMousePosition({clientX, clientY}) {
        lastMousePosition.current.x = clientX;
        lastMousePosition.current.y = clientY;
    }

    function getCollectedLettersJsx(collectedLetters) {
        floatingLettersRefs.current = [];
        for (let i = 0; i < collectedLetters.length; i++) {
            floatingLettersRefs.current.push(React.createRef());
        }
        return collectedLetters.map((letter, index) => {
            const ease = index * 0.04;
            return (
                <LetterBox
                    className="trail-letter"
                    key={letter.id}
                    letter={letter.value}
                    xpos={index}
                    ypos={0}
                    ref={floatingLettersRefs.current[index]}
                    transition={`transform ${ease}s`}
                />
            );
        });
    }

    useEffect(() => {
        function update() {
            rAFIndex.current = requestAnimationFrame(update);
            updateCollectedLettersPosition();
        }

        function updateCollectedLettersPosition() {
            for (let i = 0; i < collectedLetters.length; i++) {
                const xpos = lastMousePosition.current.x + i + 10;
                const ypos = lastMousePosition.current.y;
                floatingLettersRefs.current[i].current.style.transform = `translate(${xpos}px, ${ypos}px)`;
            }
        }

        // Cancel the existing rAF
        cancelAnimationFrame(rAFIndex.current);

        if (gameMode === ANSWERING_GAME_MODE) {
            document.addEventListener('mousemove', registerMousePosition);
            rAFIndex.current = requestAnimationFrame(update);
        }

        return () => {
            document.removeEventListener('mousemove', registerMousePosition);
        };
    }, [gameMode, collectedLetters]);

    return (
        <StateContext.Provider value={wordSearchGameState}>
            <div className="App" onMouseUp={onMouseUp}>
                <Masthead />
                <WordsPanel />
                <Board />
                {gameMode === ANSWERING_GAME_MODE ? getCollectedLettersJsx(collectedLetters) : null}
                {(pendingConfirmation || showIntro) && <Backdrop />}
                {pendingConfirmation && (
                    <ConfirmationModal onConfirm={onConfirmPendingAction} onCancel={onCancelConfirmation}>
                        {pendingConfirmation.msg}
                    </ConfirmationModal>
                )}

                {showIntro && <IntroModal onClose={onIntroClose}></IntroModal>}
                <div align="right" className="footer">
                    <span>{gameMode}</span>
                </div>
            </div>
        </StateContext.Provider>
    );
};

export default App;
